import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { parseApiError } from '@saas-kit/lib';
import {
  Card,
  CardContent,
  EmptyState,
  ErrorBanner,
  PageHeader,
  Spinner,
  toast,
} from '@saas-kit/ui';

import {
  listSettings,
  updateSetting,
  type AdminSetting,
  type SettingValue,
} from './api';
import { SettingRow } from './SettingRow';

interface UpdateInput {
  key: string;
  value: SettingValue;
}

export function SettingsPage(): JSX.Element {
  const { t } = useTranslation('adminSettings');
  const queryClient = useQueryClient();

  const settingsQuery = useQuery({
    queryKey: ['admin-settings'],
    queryFn: listSettings,
  });

  const updateMutation = useMutation({
    mutationFn: ({ key, value }: UpdateInput) => updateSetting(key, value),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin-settings'] });
      toast.success(t('toasts.saved'));
    },
    onError: (err: unknown) =>
      toast.error(parseApiError(err).message || t('errors.save_failed')),
  });

  async function handleSave(key: string, value: SettingValue): Promise<void> {
    await updateMutation.mutateAsync({ key, value });
  }

  const settings = settingsQuery.data ?? [];
  const isSavingKey = updateMutation.isPending
    ? (updateMutation.variables?.key ?? null)
    : null;

  return (
    <div className="space-y-6">
      <PageHeader title={t('title')} description={t('subtitle')} />

      {settingsQuery.isError ? (
        <ErrorBanner>{t('errors.load_failed')}</ErrorBanner>
      ) : null}

      <Card>
        <CardContent className="p-0">
          {settingsQuery.isLoading ? (
            <div className="flex items-center justify-center p-12">
              <Spinner />
            </div>
          ) : settings.length === 0 && !settingsQuery.isError ? (
            <EmptyState title={t('empty.title')} description={t('empty.body')} />
          ) : (
            <SettingsList
              settings={settings}
              savingKey={isSavingKey}
              onSave={handleSave}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

interface SettingsListProps {
  settings: AdminSetting[];
  savingKey: string | null;
  onSave: (key: string, value: SettingValue) => Promise<void>;
}

function SettingsList({
  settings,
  savingKey,
  onSave,
}: SettingsListProps): JSX.Element {
  return (
    <div className="divide-y divide-border px-4 sm:px-6">
      {settings.map((setting) => (
        <SettingRow
          key={setting.key}
          setting={setting}
          isSaving={savingKey === setting.key}
          onSave={onSave}
        />
      ))}
    </div>
  );
}
